import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Save } from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  documentTemplateTypes,
  DocumentTemplateType,
  findDocumentTemplate,
  saveDocumentTemplate,
} from "data/documentTemplateStore";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Label } from "ui/label";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Textarea } from "ui/textarea";

export const DocumentTemplateFormPage = ({
  mode,
}: {
  mode: "add" | "edit";
}) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const existing = mode === "edit" ? findDocumentTemplate(id) : undefined;
  const { setPageHeader } = usePageHeader();
  const [type, setType] = useState<DocumentTemplateType>(
    existing?.type ?? "Appointment Letter",
  );
  const [html, setHtml] = useState(existing?.html ?? "");
  const [error, setError] = useState("");
  useEffect(() => {
    setType(existing?.type ?? "Appointment Letter");
    setHtml(existing?.html ?? "");
    setError("");
  }, [existing?.type, existing?.html, id, mode]);
  useEffect(() => {
    setPageHeader({
      title:
        mode === "add" ? "Add Document Template" : "Edit Document Template",
      icon: <FileText className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [mode, setPageHeader]);
  const back = () => navigate("/document-template-master");
  const save = () => {
    try {
      saveDocumentTemplate(type, html, mode === "edit" ? id : undefined);
      toast.success("Mock template saved.");
      back();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save template.");
    }
  };
  if (mode === "edit" && !existing)
    return (
      <Card>
        <CardContent className="space-y-4 p-6">
          <p>Select an existing template from the listing to edit.</p>
          <Button onClick={back}>Back to Templates</Button>
        </CardContent>
      </Card>
    );
  return (
    <div className="space-y-4 pb-8">
      <Card className="border-sky-200 bg-white shadow-sm">
        <CardContent className="!p-4 sm:!p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full sm:w-72">
              <SelectFieldset label="Choose Template" required>
                <Select
                  value={type}
                  onValueChange={(value) => {
                    setType(value as DocumentTemplateType);
                    setError("");
                  }}
                >
                  <SelectTriggerUI aria-label="Choose Template">
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    {documentTemplateTypes.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SelectFieldset>
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-red-400 text-white sm:flex-none"
                onClick={back}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#5B9BD5] text-white sm:flex-none"
                onClick={save}
              >
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-sky-200 bg-white shadow-sm">
        <CardContent className="space-y-4 !p-4 sm:!p-6">
          <Label
            htmlFor="document-template-html"
            className="block font-semibold text-[#1e3a5f]"
          >
            Paste Raw HTML Code
          </Label>
          <Textarea
            id="document-template-html"
            value={html}
            onChange={(e) => {
              setHtml(e.target.value);
              setError("");
            }}
            placeholder="Paste your document template HTML here"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-invalid={!!error}
            aria-describedby={error ? "template-error" : undefined}
            className="min-h-[50vh] border-dashed border-sky-200 bg-white p-4 font-mono text-sm"
          />
          {error && (
            <p
              id="template-error"
              role="alert"
              className="text-xs text-red-500"
            >
              {error}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
